/**
 * Fudeto — web entry.
 *
 * Boot order:
 *   1. mount #app + critical CSS
 *   2. detect platform (TG / LINE / iOS / Android / PWA / Web / Discord)
 *   3. initialize i18n (resolves navigator.language → en / ja / zh-Hant / zh-Hans)
 *   4. initialize profile state from IndexedDB (or seed defaults)
 *   5. apply theme + a11y prefs
 *   6. register routes (core path eager; everything else lazy-imported)
 *   7. start router; first nav decides O1 (cold install) vs G1 (returning).
 */
import { initI18n } from './i18n/runtime';
import { detectPlatform } from './platform/detect';
import { initPwa } from './platform/pwa';
import { initTg } from './platform/tg';
import { initProfile } from './state/profile';
import { initSync } from './state/sync';
import { maybeMountCookieBanner } from './ui/cookie-banner';
import { type ScreenFactory, defineRoute, startRouter } from './ui/router';
import './styles/tokens.css';
import './styles/base.css';
import './styles/screens.css';

import home from './ui/screens/g1-home';
import play from './ui/screens/g2-play';
import solve from './ui/screens/g4-solve';
// Core path (eager — always reachable cold)
import welcome from './ui/screens/o1-welcome';

/** Lazy-imported screen — Vite emits a separate chunk per import path. */
function lazy(
  loader: () => Promise<Record<string, unknown>>,
  exportName = 'default',
): ScreenFactory {
  return async (ctx) => {
    const mod = await loader();
    const factory = mod[exportName] as ScreenFactory | undefined;
    if (!factory) throw new Error(`lazy screen: missing export ${exportName}`);
    return factory(ctx);
  };
}

function applyTheme(theme: string): void {
  document.documentElement.dataset['theme'] = theme;
}

function registerRoutes(): void {
  // Onboarding
  defineRoute('/welcome', welcome);
  defineRoute('/onboarding/welcome', welcome);
  // tutorial() returns a factory taking RouteContext, so we wrap it.
  defineRoute('/onboarding/1', async (ctx) =>
    (await import('./ui/screens/onboarding')).tutorial(1)(ctx),
  );
  defineRoute('/onboarding/2', async (ctx) =>
    (await import('./ui/screens/onboarding')).tutorial(2)(ctx),
  );
  defineRoute('/onboarding/3', async (ctx) =>
    (await import('./ui/screens/onboarding')).tutorial(3)(ctx),
  );
  defineRoute(
    '/onboarding/invite',
    lazy(() => import('./ui/screens/onboarding'), 'invite'),
  );
  defineRoute(
    '/onboarding/notifications',
    lazy(() => import('./ui/screens/onboarding'), 'notifications'),
  );

  // Core game
  defineRoute('/', home);
  defineRoute('/play/:date', play);
  defineRoute('/solve/:date', solve);
  defineRoute(
    '/practice',
    lazy(() => import('./ui/screens/g3-practice')),
  );
  defineRoute(
    '/share/:date',
    lazy(() => import('./ui/screens/g5-share')),
  );
  defineRoute(
    '/review/:date',
    lazy(() => import('./ui/screens/review')),
  );

  // Variants
  defineRoute(
    '/streak/:n',
    lazy(() => import('./ui/screens/variants'), 'streakMilestone'),
  );
  defineRoute(
    '/segment/:tier',
    lazy(() => import('./ui/screens/variants'), 'segmentPromotion'),
  );
  defineRoute(
    '/mode/:mode',
    lazy(() => import('./ui/screens/variants'), 'modeHeadsUp'),
  );
  defineRoute(
    '/season/:id',
    lazy(() => import('./ui/screens/variants'), 'seasonalBanner'),
  );

  // Progression
  defineRoute(
    '/calendar',
    lazy(() => import('./ui/screens/progression'), 'gemCalendar'),
  );
  defineRoute(
    '/stats',
    lazy(() => import('./ui/screens/progression'), 'personalStats'),
  );
  defineRoute(
    '/profile',
    lazy(() => import('./ui/screens/progression'), 'personalStats'),
  );
  defineRoute(
    '/rating',
    lazy(() => import('./ui/screens/progression'), 'rating'),
  );
  defineRoute(
    '/achievements',
    lazy(() => import('./ui/screens/progression'), 'achievements'),
  );
  defineRoute(
    '/leaderboard',
    lazy(() => import('./ui/screens/progression'), 'leaderboard'),
  );
  defineRoute(
    '/leaderboard/country',
    lazy(() => import('./ui/screens/progression'), 'countryLeaderboard'),
  );
  defineRoute(
    '/country/:code',
    lazy(() => import('./ui/screens/progression'), 'countryDetail'),
  );
  defineRoute(
    '/wrapped',
    lazy(() => import('./ui/screens/progression'), 'wrapped'),
  );
  defineRoute(
    '/season-end',
    lazy(() => import('./ui/screens/progression'), 'seasonEnd'),
  );

  // Monetization
  defineRoute(
    '/ad-gate',
    lazy(() => import('./ui/screens/monetization'), 'adGate'),
  );
  defineRoute(
    '/ad-watch',
    lazy(() => import('./ui/screens/monetization'), 'adWatch'),
  );
  defineRoute(
    '/iap/unlimited',
    lazy(() => import('./ui/screens/monetization'), 'iapUnlimited'),
  );
  defineRoute(
    '/iap/pro',
    lazy(() => import('./ui/screens/monetization'), 'iapPro'),
  );
  defineRoute(
    '/iap/restore',
    lazy(() => import('./ui/screens/monetization'), 'iapRestore'),
  );

  // Referral
  defineRoute(
    '/invite',
    lazy(() => import('./ui/screens/referral'), 'inviteShare'),
  );
  defineRoute(
    '/invite/in/:code',
    lazy(() => import('./ui/screens/referral'), 'inviteeOnboarding'),
  );
  defineRoute(
    '/invite/reward',
    lazy(() => import('./ui/screens/referral'), 'rewardUnlock'),
  );
  defineRoute(
    '/invite/challenge/:code',
    lazy(() => import('./ui/screens/referral'), 'friendChallenge'),
  );

  // Settings
  defineRoute(
    '/settings',
    lazy(() => import('./ui/screens/settings'), 'settingsHome'),
  );
  defineRoute(
    '/settings/themes',
    lazy(() => import('./ui/screens/settings'), 'themesScreen'),
  );
  defineRoute(
    '/settings/language',
    lazy(() => import('./ui/screens/settings'), 'languageScreen'),
  );
  defineRoute(
    '/settings/notifications',
    lazy(() => import('./ui/screens/settings'), 'notificationsScreen'),
  );
  defineRoute(
    '/settings/accessibility',
    lazy(() => import('./ui/screens/settings'), 'accessibilityScreen'),
  );
  defineRoute(
    '/settings/country',
    lazy(() => import('./ui/screens/settings'), 'countryScreen'),
  );
  defineRoute(
    '/settings/privacy',
    lazy(() => import('./ui/screens/settings'), 'privacyScreen'),
  );
  defineRoute(
    '/about',
    lazy(() => import('./ui/screens/legal'), 'about'),
  );

  // Edge states
  defineRoute(
    '/offline',
    lazy(() => import('./ui/screens/edge'), 'offline'),
  );
  defineRoute(
    '/loading',
    lazy(() => import('./ui/screens/edge'), 'loading'),
  );
  defineRoute(
    '/gen-failed',
    lazy(() => import('./ui/screens/edge'), 'generationFailed'),
  );
  defineRoute(
    '/maintenance',
    lazy(() => import('./ui/screens/edge'), 'maintenance'),
  );
  defineRoute(
    '/daily-reset',
    lazy(() => import('./ui/screens/edge'), 'dailyReset'),
  );
  defineRoute(
    '/error',
    lazy(() => import('./ui/screens/edge'), 'errorToast'),
  );
  defineRoute(
    '/lifecycle/:kind',
    lazy(() => import('./ui/screens/edge'), 'lifecycle'),
  );

  // Legal (lazy — big text blocks)
  defineRoute(
    '/legal/privacy',
    lazy(() => import('./ui/screens/legal'), 'privacy'),
  );
  defineRoute(
    '/legal/terms',
    lazy(() => import('./ui/screens/legal'), 'terms'),
  );
  defineRoute(
    '/legal/cookies',
    lazy(() => import('./ui/screens/legal'), 'cookies'),
  );
  defineRoute(
    '/legal/accessibility',
    lazy(() => import('./ui/screens/legal'), 'accessibility'),
  );
  defineRoute(
    '/legal/coppa',
    lazy(() => import('./ui/screens/legal'), 'coppa'),
  );
  defineRoute(
    '/legal/about',
    lazy(() => import('./ui/screens/legal'), 'about'),
  );
}

async function boot(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) throw new Error('#app missing');

  const platform = detectPlatform();
  document.documentElement.dataset['platform'] = platform.name;
  if (platform.name === 'telegram') initTg();
  if (platform.name === 'web' || platform.name === 'pwa') initPwa();

  // Boot locale: profile override (if user explicitly chose one) wins over navigator.language.
  const store = await initProfile();
  const initialLang = store.get().localeOverride ?? navigator.language;
  await initI18n(initialLang);
  applyTheme(store.get().theme);
  store.subscribe((s) => applyTheme(s.theme));
  initSync();
  maybeMountCookieBanner(platform);

  registerRoutes();

  // Pick the initial hash BEFORE starting the router so we don't double-mount.
  if (location.hash === '' || location.hash === '#') {
    const initial = store.get().onboarded ? '/' : '/welcome';
    history.replaceState(null, '', `#${initial}`);
  }
  startRouter(app);
}

boot().catch((err) => {
  console.error('boot failed', err);
  const app = document.getElementById('app');
  if (app) {
    app.innerHTML = '<p style="text-align:center;padding:24px;">Unable to load Fudeto.</p>';
  }
});
